import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { NAV } from "@/constants/testIds";
import api from "@/lib/api";
import NotificationsBell from "@/components/layout/NotificationsBell";
import Topbar from "@/components/layout/Topbar";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { useNotificationSocket } from "@/lib/useNotificationSocket";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  LayoutDashboard, Inbox, Users2, KanbanSquare, Calendar, Bot,
  BarChart3, Building2, Settings, LogOut, Zap, UserSquare2, GanttChartSquare, CalendarDays, Receipt, FileSignature, Package, Coins,
} from "lucide-react";
const LINKS_ALL = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testId: NAV.dashboard, exact: true, roles: ["company_admin", "division_manager", "agent"] },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/inbox", label: "Inbox", icon: Inbox, testId: NAV.inbox, roles: ["company_admin", "division_manager", "agent"] },
  { to: "/leads", label: "Leads Queue", icon: Users2, testId: NAV.leads, roles: ["company_admin", "division_manager", "agent"] },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testId: NAV.pipeline, roles: ["company_admin", "division_manager", "agent"] },
  { to: "/quotations", label: "Quotations", icon: FileSignature, testId: "nav-quotations", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/projects", label: "Projects", icon: GanttChartSquare, testId: "nav-projects", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/cost-summary", label: "Cost Summary", icon: Coins, testId: "nav-cost-summary", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/products", label: "Products", icon: Package, testId: "nav-products", roles: ["company_admin", "division_manager"] },
  { to: "/contacts", label: "Contacts", icon: UserSquare2, testId: "nav-contacts", roles: ["company_admin", "division_manager", "agent"] },
  { to: "/scheduler", label: "Scheduler", icon: Calendar, testId: NAV.scheduler, roles: ["company_admin", "division_manager", "agent"] },
  { to: "/chatbot", label: "AI Chatbot", icon: Bot, testId: NAV.chatbot, roles: ["company_admin", "division_manager"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: NAV.analytics, roles: ["company_admin", "division_manager", "agent"] },
];

const ADMIN_LINKS = [
  { to: "/divisions", label: "Divisions", icon: Building2, testId: NAV.divisions },
  { to: "/settings", label: "Settings", icon: Settings, testId: NAV.settings },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const links = LINKS_ALL.filter((l) => l.roles.includes(user?.role));
  useNotificationSocket(user);

  const { data: unread = { count: 0 } } = useQuery({
    queryKey: ["inbox-unread"],
    queryFn: () => api.get("/inbox/unread-count").then((r) => r.data),
    refetchInterval: 30000,
    enabled: !!user,
  });

  return (
    <div className="App min-h-screen grain flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 border-r border-border bg-[#0A0A0A] sticky top-0 h-screen z-20">
        <div className="px-6 py-5 border-b border-border flex items-center gap-2" data-testid={NAV.logo}>
          <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center">
            <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-base font-extrabold tracking-tight">LeadifyCRM</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Command Center</div>
          </div>
        </div>

        {/* Division switcher (company admin only) */}
        {user?.role === "company_admin" && <DivisionSwitcher />}

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-3 py-2">Workspace</div>
          {links.map((l) => (
            <NavItem key={l.to} {...l} badge={l.to === "/inbox" ? unread.count : 0} />
          ))}
          {user?.role === "company_admin" && (
            <>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold px-3 pt-4 pb-2">Company</div>
              {ADMIN_LINKS.map((l) => (<NavItem key={l.to} {...l} />))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2 rounded-sm bg-secondary/40">
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role?.replace("_", " ")}</div>
            </div>
            <button
              data-testid={NAV.logoutButton}
              onClick={logout}
              className="p-1.5 rounded-sm text-muted-foreground hover:bg-secondary hover:text-white transition-colors"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="flex items-center gap-2 px-4 py-3">
          <div className="h-7 w-7 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>
          <div className="font-display font-extrabold">LeadifyCRM</div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationsBell compact />
            <button className="text-xs uppercase tracking-widest text-muted-foreground" onClick={logout} data-testid={NAV.logoutButton+"-mobile"}>Logout</button>
          </div>
        </div>
        {user?.role === "company_admin" && <div className="px-4 pb-2"><DivisionSwitcher /></div>}
        <nav className="flex gap-1 overflow-x-auto px-2 pb-2 no-scrollbar">
          {[...links, ...(user?.role === "company_admin" ? ADMIN_LINKS : [])].map((l) => (
            <NavLink key={l.to} to={l.to} end={l.exact} data-testid={l.testId}
              className={({ isActive }) => `text-xs px-3 py-1.5 rounded-sm whitespace-nowrap border ${isActive ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>
              {l.label}{l.to === "/inbox" && unread.count > 0 ? ` (${unread.count})` : ""}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 min-w-0 pt-32 lg:pt-0 relative z-10">
        <ImpersonationBanner />
        <Topbar />
        {children}
      </main>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testId, exact, badge = 0 }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
          isActive
            ? "bg-primary/15 text-white border-l-2 border-primary"
            : "text-muted-foreground hover:bg-secondary/60 hover:text-white border-l-2 border-transparent"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      <span className="font-medium flex-1">{label}</span>
      {badge > 0 && (
        <span data-testid={testId + "-badge"} className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

function DivisionSwitcher() {
  const qc = useQueryClient();
  const [value, setValue] = React.useState(localStorage.getItem("leadify_selected_division") || "all");
  const { data: divs = [] } = useQuery({ queryKey: ["divs"], queryFn: () => api.get("/divisions").then((r) => r.data) });

  const onChange = (v) => {
    setValue(v);
    if (v === "all") localStorage.removeItem("leadify_selected_division");
    else localStorage.setItem("leadify_selected_division", v);
    // refresh every query so scope applies
    qc.invalidateQueries();
  };

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-1.5 flex items-center gap-1">
        <Building2 className="h-3 w-3" />Active Division
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="division-switcher" className="rounded-sm bg-secondary/40 h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Divisions (Company-wide)</SelectItem>
          {divs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {d.currency}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
